/**
 * Permission Checking Utility
 * 
 * This module provides utilities for checking user permissions based on their roles
 * in a company. It integrates with the role-based permission system stored in the database.
 */

import { CompanyRole, UserPermissionsInCompany, RolePermissions } from '../stores/storeTypes';
import { unifiedUserStore } from '../stores/unifiedUserStore';
import { getEffectivePermissions, isDevelopmentMode, useDevRoleOverride } from './devRoleOverride';

/**
 * Built-in roles that are always available for every company
 * These roles are standard and should not depend on database entries
 */
const BUILT_IN_ROLES: Omit<CompanyRole, 'company_id' | 'id'>[] = [
  {
    role_name: 'Owner',
    is_built_in: true,
    description: 'Full access to all company features and settings',
    permissions: {
      '*': true, // Wildcard permission for all actions
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    role_name: 'Contributor',
    is_built_in: true,
    description: 'Can view and edit content, but cannot manage users or company settings',
    permissions: {
      'games.read': true,
      'games.write': true,
      'glossaries.read': true,
      'glossaries.write': true,
      'content.view': true,
      'content.create': true,
      'content.edit': true,
      'translations.view': true,
      'translations.translate': true,
      'translations.review': true,
      'pages.library.view': true,
      'pages.templates.view': true,
      'pages.documents.view': true,
      'pages.dialogs.view': true,
      'pages.logicFlows.view': true,
      'pages.tms.view': true,
      'pages.types.view': true,
      'pages.ui.view': true,
      'pages.images.view': true,
      'pages.relationships.view': true,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

/**
 * Get built-in roles for a specific company
 * These roles are always available regardless of database state
 */
function getBuiltInRoles(companyId: string): CompanyRole[] {
  return BUILT_IN_ROLES.map((role) => ({
    ...role,
    id: `built-in-${role.role_name.toLowerCase()}-${companyId}`,
    company_id: companyId,
  }));
}

/**
 * Get all roles for a specific company
 * Always includes built-in roles (Owner, Contributor) plus any custom roles from the database
 */
export function getCompanyRoles(companyId: string): CompanyRole[] {
  const dataArray = unifiedUserStore.getData();
  
  // Always start with built-in roles
  const builtInRoles = getBuiltInRoles(companyId);
  
  // If no data available, return just the built-in roles
  if (!dataArray || dataArray.length === 0) return builtInRoles;
  
  const data = dataArray[0];
  if (!data?.roles) return builtInRoles;
  
  // Get custom roles from the database
  const customRoles = data.roles.filter(
    role => role.company_id === companyId && !role.is_built_in
  );
  
  // Combine built-in roles with custom roles
  // Filter out any database entries for built-in roles to avoid duplicates
  return [...builtInRoles, ...customRoles];
}

/**
 * Get a specific role by ID
 */
export function getRoleById(roleId: string): CompanyRole | undefined {
  const dataArray = unifiedUserStore.getData();
  if (!dataArray || dataArray.length === 0) return undefined;
  
  const data = dataArray[0];
  if (!data?.roles) return undefined;
  
  return data.roles.find(role => role.id === roleId);
}

/**
 * Get a role by name within a company
 * Checks both built-in and custom roles
 */
export function getRoleByName(companyId: string, roleName: string): CompanyRole | undefined {
  const roles = getCompanyRoles(companyId); // This now includes built-in roles
  return roles.find(role => role.role_name.toLowerCase() === roleName.toLowerCase());
}

/**
 * Get current user's permissions for a specific company
 */
export function getUserPermissions(companyId: string): UserPermissionsInCompany | null {
  const dataArray = unifiedUserStore.getData();
  if (!dataArray || dataArray.length === 0) return null;
  
  const data = dataArray[0];
  if (!data?.userPermissions) return null;
  
  return data.userPermissions[companyId] || null;
}

/**
 * Check if user has a specific permission in a company
 * 
 * @param companyId - The company ID to check permissions for
 * @param permissionKey - The permission key to check (e.g., "users.invite", "games.create")
 * @returns true if user has the permission, false otherwise
 */
export function hasPermission(companyId: string, permissionKey: string): boolean {
  // In development mode, check for role override first
  if (isDevelopmentMode()) {
    const { overrideRoleId } = useDevRoleOverride.getState();
    if (overrideRoleId) {
      const effectivePerms = getEffectivePermissions(companyId);
      
      // If no permissions are set at all, treat as wildcard (full access)
      const hasAnyPermissions = Object.keys(effectivePerms).length > 0;
      if (!hasAnyPermissions) {
        return true;
      }
      
      // Check for wildcard permission (owner)
      if (effectivePerms['*'] === true) {
        return true;
      }
      
      // Check for specific permission
      return effectivePerms[permissionKey] === true;
    }
  }
  
  // Otherwise use actual user permissions
  const userPerms = getUserPermissions(companyId);
  
  if (!userPerms) {
    return false;
  }
  
  // If no permissions are set at all, treat as wildcard (full access)
  const hasAnyPermissions = Object.keys(userPerms.permissions).length > 0;
  if (!hasAnyPermissions) {
    return true;
  }
  
  // Check for wildcard permission (owner)
  if (userPerms.permissions['*'] === true) {
    return true;
  }
  
  // Check for specific permission
  return userPerms.permissions[permissionKey] === true;
}

/**
 * Check if user has any of the specified permissions in a company
 * 
 * @param companyId - The company ID to check permissions for
 * @param permissionKeys - Array of permission keys to check
 * @returns true if user has at least one of the permissions, false otherwise
 */
export function hasAnyPermission(companyId: string, permissionKeys: string[]): boolean {
  return permissionKeys.some(key => hasPermission(companyId, key));
}

/**
 * Check if user has all of the specified permissions in a company
 * 
 * @param companyId - The company ID to check permissions for
 * @param permissionKeys - Array of permission keys to check
 * @returns true if user has all of the permissions, false otherwise
 */
export function hasAllPermissions(companyId: string, permissionKeys: string[]): boolean {
  return permissionKeys.every(key => hasPermission(companyId, key));
}

/**
 * Check if user is an owner of a company
 * Note: This checks the role name, not just permissions
 */
export function isOwner(companyId: string): boolean {
  // In development mode, check for role override first
  if (isDevelopmentMode()) {
    const { overrideRoleId } = useDevRoleOverride.getState();
    if (overrideRoleId) {
      const roles = getCompanyRoles(companyId);
      const overrideRole = roles.find(r => r.id === overrideRoleId);
      if (overrideRole) {
        return overrideRole.role_name.toLowerCase() === 'owner';
      }
    }
  }
  
  const userPerms = getUserPermissions(companyId);
  
  if (!userPerms) {
    return false;
  }
  
  return userPerms.role.toLowerCase() === 'owner';
}

/**
 * Get user's role name in a company
 */
export function getUserRole(companyId: string): string | null {
  // In development mode, check for role override first
  if (isDevelopmentMode()) {
    const { overrideRoleId } = useDevRoleOverride.getState();
    if (overrideRoleId) {
      const roles = getCompanyRoles(companyId);
      const overrideRole = roles.find(r => r.id === overrideRoleId);
      if (overrideRole) {
        return overrideRole.role_name;
      }
    }
  }
  
  const userPerms = getUserPermissions(companyId);
  return userPerms?.role || null;
}

/**
 * Get user's ACTUAL role name in a company (ignores dev overrides)
 * Use this when you need to display the user's real role
 */
export function getActualUserRole(companyId: string): string | null {
  const userPerms = getUserPermissions(companyId);
  return userPerms?.role || null;
}

/**
 * Get all permissions for a specific role
 */
export function getRolePermissions(roleId: string): RolePermissions {
  const role = getRoleById(roleId);
  return role?.permissions || {};
}

/**
 * Check if a role is built-in (cannot be deleted)
 */
export function isBuiltInRole(roleId: string): boolean {
  const role = getRoleById(roleId);
  return role?.is_built_in || false;
}

/**
 * Permission categories for UI organization
 */
export const PERMISSION_CATEGORIES = {
  GAMES: {
    READ: 'games.read',
    WRITE: 'games.write',
    CREATE: 'games.create',
    DELETE: 'games.delete',
  },
  USERS: {
    VIEW: 'users.view',
    INVITE: 'users.invite',
    MANAGE: 'users.manage',
    REMOVE: 'users.remove',
  },
  SETTINGS: {
    VIEW: 'settings.view',
    MODIFY: 'settings.modify',
  },
  GLOSSARIES: {
    READ: 'glossaries.read',
    WRITE: 'glossaries.write',
  },
  ROLES: {
    MANAGE: 'roles.manage',
  },
  TRANSLATIONS: {
    VIEW: 'translations.view',
    TRANSLATE: 'translations.translate',
    REVIEW: 'translations.review',
    APPROVE: 'translations.approve',
  },
  CONTENT: {
    VIEW: 'content.view',
    CREATE: 'content.create',
    EDIT: 'content.edit',
    DELETE: 'content.delete',
  },
  ANALYTICS: {
    VIEW: 'analytics.view',
    EXPORT: 'analytics.export',
  },
} as const;

/**
 * Get all available permission keys
 */
export function getAllPermissionKeys(): string[] {
  const keys: string[] = [];
  
  for (const category of Object.values(PERMISSION_CATEGORIES)) {
    keys.push(...Object.values(category));
  }
  
  return keys;
}

/**
 * Get permission display information
 */
export function getPermissionInfo(permissionKey: string): { name: string; description: string; category: string } {
  const permissionMap: { [key: string]: { name: string; description: string; category: string } } = {
    // Games
    'games.read': { name: 'View Games', description: 'Can view game list and details', category: 'Games' },
    'games.write': { name: 'Edit Games', description: 'Can modify game settings', category: 'Games' },
    'games.create': { name: 'Create Games', description: 'Can create new games', category: 'Games' },
    'games.delete': { name: 'Delete Games', description: 'Can delete games', category: 'Games' },
    
    // Users
    'users.view': { name: 'View Users', description: 'Can view company members', category: 'Users' },
    'users.invite': { name: 'Invite Users', description: 'Can invite new users', category: 'Users' },
    'users.manage': { name: 'Manage Users', description: 'Can modify user roles and settings', category: 'Users' },
    'users.remove': { name: 'Remove Users', description: 'Can remove users from company', category: 'Users' },
    
    // Settings
    'settings.view': { name: 'View Settings', description: 'Can access company settings', category: 'Settings' },
    'settings.modify': { name: 'Edit Settings', description: 'Can modify company settings', category: 'Settings' },
    
    // Glossaries
    'glossaries.read': { name: 'View Glossaries', description: 'Can view glossary entries', category: 'Glossaries' },
    'glossaries.write': { name: 'Edit Glossaries', description: 'Can create and modify glossary entries', category: 'Glossaries' },
    
    // Roles
    'roles.manage': { name: 'Manage Roles', description: 'Can create and modify user roles', category: 'Roles' },
    
    // Translations
    'translations.view': { name: 'View Translations', description: 'Can view translation status', category: 'Translations' },
    'translations.translate': { name: 'Create Translations', description: 'Can create translations', category: 'Translations' },
    'translations.review': { name: 'Review Translations', description: 'Can review translations', category: 'Translations' },
    'translations.approve': { name: 'Approve Translations', description: 'Can approve translations', category: 'Translations' },
    
    // Content
    'content.view': { name: 'View Content', description: 'Can view strings and documents', category: 'Content' },
    'content.create': { name: 'Create Content', description: 'Can create new content', category: 'Content' },
    'content.edit': { name: 'Edit Content', description: 'Can modify content', category: 'Content' },
    'content.delete': { name: 'Delete Content', description: 'Can delete content', category: 'Content' },
    
    // Analytics
    'analytics.view': { name: 'View Analytics', description: 'Can view analytics and reports', category: 'Analytics' },
    'analytics.export': { name: 'Export Data', description: 'Can export data and reports', category: 'Analytics' },
  };
  
  return permissionMap[permissionKey] || { name: permissionKey, description: '', category: 'Other' };
}

/**
 * Page permission keys mapping
 * Maps page IDs to their permission keys
 */
export const PAGE_PERMISSIONS: { [key: string]: string } = {
  home: 'pages.home.view',
  glossary: 'pages.library.view', // Alias for library (sidebar uses "glossary")
  library: 'pages.library.view',
  templates: 'pages.templates.view',
  blueprints: 'pages.templates.view', // Alias for templates
  documents: 'pages.documents.view',
  dialogs: 'pages.dialogs.view',
  tms: 'pages.tms.view',
  textManagement: 'pages.textManagement.view',
  'text-management': 'pages.textManagement.view', // Alias for textManagement
  tags: 'pages.types.view', // Alias for types (sidebar uses "tags")
  'types-and-tags': 'pages.types.view', // Alias for types (sidebar uses "types-and-tags")
  types: 'pages.types.view',
  variables: 'pages.types.view', // Variables use types permission
  globals: 'pages.globals.view',
  ui: 'pages.ui.view',
  images: 'pages.images.view',
  context: 'pages.context.view',
  relationship: 'pages.relationships.view', // Alias for relationships (sidebar uses "relationship")
  relationships: 'pages.relationships.view',
  logic: 'pages.logicFlows.view', // Alias for logicFlows (sidebar uses "logic")
  logicFlows: 'pages.logicFlows.view',
  'logic-flows': 'pages.logicFlows.view', // Alias for logicFlows
  settings: 'pages.settings.view',
  profile: 'pages.profile.view',
};

/**
 * Check if user has access to a specific page
 * 
 * @param companyId - The company ID to check permissions for
 * @param pageId - The page ID to check (e.g., 'library', 'templates')
 * @returns true if user has access to the page, false otherwise
 */
export function hasPageAccess(companyId: string, pageId: string): boolean {
  // Special cases - always accessible (critical navigation/system pages)
  if (pageId === 'settings' || pageId === 'logout' || pageId === 'home') {
    return true;
  }
  
  const permissionKey = PAGE_PERMISSIONS[pageId];
  
  // Unknown pages are accessible by default (for backwards compatibility)
  if (!permissionKey) return true;
  
  // Check if user has the specific page permission
  return hasPermission(companyId, permissionKey);
}

